import React from "react";
import { observer } from "mobx-react";
import ewtStore from "../EWTStore";
import { reaction } from "mobx";
import { StickyTable, Row, Cell } from "react-sticky-table";
import RoundSelector from "./RoundSelector";
import { Button } from "antd";
import { EditFilled, PlusOutlined } from "@ant-design/icons";
import { DataRoundEdit } from "./DataRoundEdit";
import { DataGroupEdit } from "./DataGroupEdit";
import { theme, formatPlayerHandicap } from "../theme";

const getRoundGroups = (round) => {
  if (!round || !round.groups) return [];
  return Object.keys(round.groups)
    .map((groupID) => {
      const group = round.groups[groupID];
      return { ...group, id: groupID };
    })
    .sort((a, b) => parseInt(a.groupNumber, 10) - parseInt(b.groupNumber, 10));
};

const buildPairingRows = (round) => {
  if (!round || !round.groups || Object.keys(round.groups).length === 0) {
    return [];
  }
  const groups = getRoundGroups(round);
  const rows = [];
  groups.forEach((group) => {
    const groupPlayerIDs = round.players
      ? Object.keys(round.players).filter(
          (playerID) => round.players[playerID].groupID === group.id
        )
      : [];
    if (groupPlayerIDs.length === 0) {
      rows.push({ id: -1, groupID: group.id });
    } else {
      groupPlayerIDs.forEach((playerID) => {
        rows.push({ ...round.players[playerID], id: playerID, groupID: group.id });
      });
    }
  });
  return rows;
};

const getInitialRoundID = () => {
  if (ewtStore.currentRoundID) return ewtStore.currentRoundID;
  if (ewtStore.rounds) {
    const roundIDs = Object.keys(ewtStore.rounds);
    if (roundIDs.length > 0) return roundIDs[0];
  }
  return null;
};

const getRoundPlayers = (round) => {
  if (!round) return null;
  let players = buildPairingRows(round);
  if (players.length === 0 && round.players) {
    players = Object.keys(round.players).map((playerID) => {
      const p = round.players[playerID];
      return { ...p, id: playerID };
    });
    players = players.sort(function (a, b) {
      if (a.groupID === b.groupID) return 0;
      if (a.groupID > b.groupID) return 1;
      return -1;
    });
  }
  return players;
};

const newRoundTemplate = () => ({
  orgID: "1",
  courseID: "1",
  finalized: false,
  fixedPairings: false,
  open: false,
  practice: false,
  startingHole: "1",
  tournamentID: ewtStore.tournament.id,
  groups: {},
  players: {},
});

const Pairings = observer(
  class Pairings extends React.Component {
    constructor(props) {
      super(props);
      this.disposeCurrentRoundReaction = reaction(
        () => ewtStore.currentRoundID,
        (currentRoundID) => {
          if (!this._isMounted || this.props.all) return;
          this.roundChanged(currentRoundID);
        }
      );
      const initialRoundID = getInitialRoundID();
      const initialRound =
        initialRoundID && ewtStore.rounds && ewtStore.rounds[initialRoundID];
      this.state = {
        roundID: initialRoundID,
        players: getRoundPlayers(initialRound),
        round: initialRound || null,
        showRoundEdit: false,
        editRound: null,
        showGroupEdit: false,
        editGroup: null,
        allPlayers: null,
      };
      this.roundChanged = this.roundChanged.bind(this);
      this.openAddRound = this.openAddRound.bind(this);
      this.openEditRound = this.openEditRound.bind(this);
      this.closeRoundEdit = this.closeRoundEdit.bind(this);
      this.openAddGroup = this.openAddGroup.bind(this);
      this.openEditGroup = this.openEditGroup.bind(this);
      this.closeGroupEdit = this.closeGroupEdit.bind(this);
    }
    roundChanged(roundID) {
      this.setState({ roundID: roundID });
      var round = roundID && ewtStore.rounds && ewtStore.rounds[roundID];
      if (!round) {
        this.setState({ round: null, players: null });
        return;
      }
      this.setState({
        round: round,
        players: getRoundPlayers(round),
      });
    }

    openAddRound() {
      this.setState({ showRoundEdit: true, editRound: newRoundTemplate() });
    }

    openEditRound() {
      if (!this.state.round) return;
      this.setState({
        showRoundEdit: true,
        editRound: JSON.parse(JSON.stringify(this.state.round)),
      });
    }

    closeRoundEdit() {
      this.setState({ showRoundEdit: false, editRound: null });
    }

    async ensureAllPlayers() {
      if (this.state.allPlayers) return this.state.allPlayers;
      const allPlayers = await ewtStore.getAllPlayers();
      this.setState({ allPlayers });
      return allPlayers;
    }

    async openAddGroup() {
      if (!this.state.roundID) return;
      const group = ewtStore.buildNewRoundGroup(this.state.roundID);
      if (!group) return;
      await this.ensureAllPlayers();
      this.setState({
        showGroupEdit: true,
        editGroup: group,
      });
    }

    async openEditGroup(group) {
      await this.ensureAllPlayers();
      this.setState({
        showGroupEdit: true,
        editGroup: JSON.parse(JSON.stringify(group)),
      });
    }

    closeGroupEdit() {
      this.setState({ showGroupEdit: false, editGroup: null });
      this.roundChanged(this.state.roundID);
    }

    componentDidMount() {
      this._isMounted = true;
      if (this.props.all) return;
      this.roundChanged(getInitialRoundID());
    }

    componentWillUnmount() {
      this._isMounted = false;
      if (this.disposeCurrentRoundReaction) this.disposeCurrentRoundReaction();
    }

    render() {
      const players = this.state.players;
      const round = this.state.round;
      const isAdmin = ewtStore.player && ewtStore.player.admin;
      const cellHeader = {
        backgroundColor: theme.navy,
        border: "none",
        padding: "6px 4px",
        fontSize: 18,
        fontWeight: 600,
        color: "#FFFFFF",
        textAlign: "center",
        letterSpacing: "0.02em",
      };
      const dataCell = {
        borderBottom: `1px solid ${theme.border}`,
        borderLeft: "none",
        borderRight: "none",
        borderTop: "none",
        textAlign: "center",
        fontSize: theme.fontBase,
        padding: "4px 6px",
        color: theme.navy,
      };
      const groupCol = {
        ...dataCell,
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        fontWeight: 600,
      };
      const timeCol = {
        ...dataCell,
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        fontSize: theme.fontMd,
      };
      const playerCol = {
        ...dataCell,
        textAlign: "left",
        cursor: "pointer",
        padding: "6px 8px",
      };
      const photoWidth = 58;
      const photoHeight = 40;
      var lastGroupID = 0;
      var newGroup = false;
      const containerStyle = this.props.all
        ? {
            height: "100%",
            minHeight: 0,
            width: "100%",
            overflow: "hidden",
          }
        : {
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflow: "hidden",
          };
      return (
        <div className="flexColumn flexGrow" style={containerStyle}>
          {this.props.all && (
            <div
              className="flexRow"
              style={{
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 8,
                margin: "2px 4px 4px",
                flexShrink: 0,
              }}
            >
              <RoundSelector
                roundID={this.state.roundID}
                handleEvent={this.roundChanged}
                compact
              />
              {isAdmin && (
                <>
                  <Button
                    title="Edit Round"
                    aria-label="Edit Round"
                    style={{
                      fontSize: 20,
                      height: 38,
                      width: 38,
                      padding: 0,
                      flexShrink: 0,
                    }}
                    disabled={!round}
                    onClick={this.openEditRound}
                    icon={<EditFilled />}
                  />
                  <Button
                    title="Add Round"
                    aria-label="Add Round"
                    style={{
                      fontSize: 20,
                      height: 38,
                      width: 38,
                      padding: 0,
                      flexShrink: 0,
                    }}
                    onClick={this.openAddRound}
                    icon={<PlusOutlined />}
                  />
                </>
              )}
            </div>
          )}
          {!round && this.props.all && (
            <div style={{ marginLeft: 10, fontSize: 18, color: theme.slate }}>
              {Object.keys(ewtStore.rounds || {}).length === 0
                ? "No rounds yet. Add a round to set up pairings."
                : "Select a round to view pairings."}
            </div>
          )}
          {round &&
            !round.players &&
            (!round.groups || Object.keys(round.groups).length === 0) && (
            <div style={{ padding: 24, textAlign: "center", color: theme.slate, fontSize: 18, lineHeight: 1.5 }}>
              Pairings TBD<br />Determined by Standings
            </div>
          )}
          {round && (
            <>
              <div
                className="flexRow"
                style={{
                  padding: "4px 4px 8px",
                  fontSize: 20,
                  fontWeight: 600,
                  color: theme.navy,
                  borderBottom: `1px solid ${theme.border}`,
                  marginBottom: 4,
                  flexShrink: 0,
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div className="flexGrow" style={{ textAlign: "left", minWidth: 0 }}>
                  {round.name}&nbsp;·&nbsp;{round.tees}&nbsp;Tees
                </div>
                {isAdmin && this.props.all && (
                  <Button
                    title="Add Group"
                    aria-label="Add Group"
                    style={{
                      fontSize: 20,
                      height: 34,
                      width: 34,
                      padding: 0,
                      flexShrink: 0,
                    }}
                    disabled={!round}
                    onClick={this.openAddGroup}
                    icon={<PlusOutlined />}
                  />
                )}
              </div>
              {ewtStore.players && players && (
                <div
                  className="flexGrow flexColumn cupPanel--bleed cupPanel--pairings"
                  style={{ minHeight: 0, flex: 1, overflow: "hidden" }}
                >
                <StickyTable border={0} style={{ fontSize: theme.fontBase, width: "100%", tableLayout: "fixed", height: "100%" }}>
                  <Row>
                    <Cell style={{ ...cellHeader, width: 40, minWidth: 40, maxWidth: 40 }}>#</Cell>
                    <Cell style={{ ...cellHeader, width: 56, minWidth: 56, maxWidth: 56 }}>Time</Cell>
                    <Cell style={cellHeader}>Player</Cell>
                  </Row>
                  {players.map((player, index) => {
                    newGroup = player.groupID !== lastGroupID;
                    const pictureURL =
                      player.id !== -1 &&
                      ewtStore.players[player.id] &&
                      ewtStore.players[player.id].pictureURL;
                    const group = round.groups && round.groups[player.groupID];
                    lastGroupID = player.groupID;
                    const rowBg =
                      index % 2 === 0 ? theme.surface : theme.surfaceAlt;
                    const rowStyle = { backgroundColor: rowBg };
                    return (
                      <Row key={index}>
                        <Cell style={{ ...groupCol, ...rowStyle }}>
                          {newGroup && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 6,
                              }}
                            >
                              <span>{player.groupID}</span>
                              {isAdmin &&
                                this.props.all &&
                                group && (
                                  <EditFilled
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.openEditGroup({
                                        ...group,
                                        id: player.groupID,
                                      });
                                    }}
                                    style={{
                                      fontSize: 18,
                                      cursor: "pointer",
                                      color: theme.brandBlue,
                                    }}
                                  />
                                )}
                            </div>
                          )}
                        </Cell>
                        <Cell style={{ ...timeCol, ...rowStyle }}>
                          {newGroup &&
                            round.groups[player.groupID] &&
                            round.groups[player.groupID].teeTime}
                        </Cell>
                        {player.id !== -1 && ewtStore.players[player.id] ? (
                          <Cell
                            style={{ ...playerCol, ...rowStyle }}
                            onClick={() => {
                              ewtStore.players[player.id].showRound = round;
                              ewtStore.showPlayerCard =
                                ewtStore.players[player.id];
                            }}
                          >
                            <div
                              style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500 }}>
                                  {ewtStore.players[player.id].first_name}&nbsp;
                                  {ewtStore.players[player.id].last_name}
                                  &nbsp;{ewtStore.players[player.id].position}
                                </div>
                                <div style={{ fontSize: theme.fontMd, color: theme.slate, marginTop: 2 }}>
                                  <span style={{ fontWeight: 600, color: theme.navy }}>
                                    {ewtStore.players[player.id].nickname}
                                  </span>
                                  &nbsp;&nbsp;Hcp&nbsp;
                                  {formatPlayerHandicap(
                                    ewtStore.players[player.id].handicaps[
                                      ewtStore.tournament.id
                                    ]
                                  )}
                                </div>
                              </div>
                              {pictureURL && (
                                <img
                                  src={pictureURL}
                                  alt={""}
                                  style={{
                                    cursor: "pointer",
                                    width: photoWidth,
                                    height: photoHeight,
                                    objectFit: "cover",
                                    borderRadius: 6,
                                    border: `1px solid ${theme.border}`,
                                    flexShrink: 0,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    this.setState({
                                      fullPP: this.state.fullPP
                                        ? null
                                        : pictureURL,
                                    });
                                  }}
                                />
                              )}
                            </div>
                          </Cell>
                        ) : (
                          <Cell
                            style={{
                              ...playerCol,
                              ...rowStyle,
                              color: theme.slate,
                              fontStyle: "italic",
                            }}
                          >
                            No players assigned
                          </Cell>
                        )}
                      </Row>
                    );
                  })}
                </StickyTable>
                </div>
              )}
              {round &&
                players &&
                players.length === 0 &&
                (!round.groups || Object.keys(round.groups).length === 0) && (
                  <div style={{ marginLeft: 10, fontSize: 18, marginTop: 10, color: theme.slate }}>
                    No groups yet. Add a group to assign players.
                  </div>
                )}
            </>
          )}
          {this.state.fullPP && (
            <div
              className="playerPhotoOverlay"
              onClick={() => this.setState({ fullPP: null })}
            >
              <img
                src={this.state.fullPP}
                alt=""
                onClick={() => this.setState({ fullPP: null })}
              />
            </div>
          )}
          {isAdmin && (
            <DataRoundEdit
              open={this.state.showRoundEdit}
              hideModal={this.closeRoundEdit}
              round={this.state.editRound}
            />
          )}
          {isAdmin && (
            <DataGroupEdit
              open={this.state.showGroupEdit}
              hideModal={this.closeGroupEdit}
              group={this.state.editGroup}
              players={this.state.allPlayers || []}
              roundID={this.state.roundID}
              round={round}
              tournamentID={
                ewtStore.tournament
                  ? ewtStore.tournament.id
                  : round && round.tournamentID
              }
            />
          )}
        </div>
      );
    }
  }
);

export default Pairings;
